import {
  Button,
  Checkbox,
  CheckboxProps,
  Col,
  DatePicker,
  Drawer,
  Form,
  Popconfirm,
  Row,
  Select,
  Typography,
} from "antd";
import React from "react";
import { useFetcher } from "react-router-dom";
import { useFetcherBusy } from "~/hooks/useRouteActivity";
import { NavButton } from "../InternalLink";
import { LeaseTypeTag } from "../Tags/LeaseTypeTag";
import { ROUTES, INTENTS, toPersistedLeaseDateIso } from "~/utils";
import { useTranslation } from "react-i18next";
import * as APITypes from "~/API";
import { getEntityListOptions } from "~/services/browser";
import { EntityListData, NewLeaseFormPayload } from "~/types";
import { TenantNameTag } from "../Tags";
import dayjs from "dayjs";

// TODO Translate
const LeaseTypeOptionsList: APITypes.LeaseTypeOptions[] = [
  APITypes.LeaseTypeOptions.Monthly,
  APITypes.LeaseTypeOptions.ThreeMonths,
  APITypes.LeaseTypeOptions.SixMonths,
  APITypes.LeaseTypeOptions.OneYear,
  APITypes.LeaseTypeOptions.TwoYears,
  APITypes.LeaseTypeOptions.ThreeYears,
];

export const NewLeaseButton = ({
  entityID,
  tenantList,
  unitListMap,
  entityType,
  entityLabel,
}: {
  entityID?: string;
  tenantList: APITypes.Tenant[];
  unitListMap?: EntityListData;
  entityType?: APITypes.EntityType;
  entityLabel?: string;
}) => {
  const { t } = useTranslation();
  const [showNewLeaseDrawer, setShowNewLeaseDrawer] = React.useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = React.useState(false);
  const [becomeMonthtoMonth, setBecomeMonthtoMonth] = React.useState(false);
  const [formInstance] = Form.useForm();
  const entityListOptions: { value: string; text: string }[] =
    entityID && entityLabel
      ? [{ value: entityID, text: entityLabel }]
      : getEntityListOptions(unitListMap);

  const fetcher = useFetcher();
  const isSubmitting = useFetcherBusy(fetcher);
  const toggleNewLeaseDrawer = () => setShowNewLeaseDrawer(!showNewLeaseDrawer);
  const toggleSubmitConfirm = () => setShowSubmitConfirm(!showSubmitConfirm);

  // Function to calculate end date based on lease type and start date
  const calculateEndDate = (leaseType: string, startDate: any) => {
    if (!startDate || !leaseType) return null;

    const start = dayjs(startDate);
    let endDate;

    switch (leaseType) {
      case APITypes.LeaseTypeOptions.ThreeYears:
        endDate = start.add(3, "year").subtract(1, "day");
        break;
      case APITypes.LeaseTypeOptions.TwoYears:
        endDate = start.add(2, "year").subtract(1, "day");
        break;
      case APITypes.LeaseTypeOptions.OneYear:
        endDate = start.add(1, "year").subtract(1, "day");
        break;
      case APITypes.LeaseTypeOptions.SixMonths:
        endDate = start.add(6, "months").subtract(1, "day");
        break;
      case APITypes.LeaseTypeOptions.ThreeMonths:
        endDate = start.add(3, "months").subtract(1, "day");
        break;
      case APITypes.LeaseTypeOptions.Monthly:
        endDate = start.add(1, "month").subtract(1, "day");
        break;
      default:
        return null;
    }

    return endDate;
  };

  const handleSubmit = (formValues: NewLeaseFormPayload) => {
    const toIso = (value: unknown): string => {
      if (!value) return "";
      const parsed = dayjs(value as string | number | Date | dayjs.Dayjs);
      return parsed.isValid() ? toPersistedLeaseDateIso(parsed) : "";
    };

    const payload: NewLeaseFormPayload = {
      entityID: formValues.entityID || entityID,
      entityType: entityType ?? APITypes.EntityType.Unit,
      tenantID: formValues.tenantID,
      extraOccupants: formValues.extraOccupants,
      type: formValues.type,
      startDate: toIso(formValues.startDate),
      markedDate: becomeMonthtoMonth ? toIso(formValues.markedDate) : "",
      endDate: toIso(formValues.endDate),
      normalizeRent: Boolean(formValues.normalizeRent),
    };

    fetcher.submit(
      {
        body: JSON.stringify(payload),
        intent: INTENTS._add_lease,
      },
      { method: "post" }
    );
    toggleNewLeaseDrawer();
  };

  const toggleBecomeMonthtoMonth: CheckboxProps["onChange"] = (e) => {
    setBecomeMonthtoMonth(!becomeMonthtoMonth);
  };

  // Handle lease type change
  const handleLeaseTypeChange = (value: string) => {
    const startDate = formInstance.getFieldValue("startDate");
    if (startDate && !becomeMonthtoMonth) {
      const calculatedEndDate = calculateEndDate(value, startDate);
      if (calculatedEndDate) {
        formInstance.setFieldValue("endDate", calculatedEndDate);
      }
    }
  };

  // Handle start date change
  const handleStartDateChange = (date: any) => {
    const leaseType = formInstance.getFieldValue("type");
    if (leaseType && date && !becomeMonthtoMonth) {
      const calculatedEndDate = calculateEndDate(leaseType, date);
      if (calculatedEndDate) {
        formInstance.setFieldValue("endDate", calculatedEndDate);
      }
    }
  };

  // Set entityID when drawer opens or entityID changes
  React.useEffect(() => {
    if (showNewLeaseDrawer && entityID) {
      formInstance.setFieldValue("entityID", entityID);
    }
  }, [showNewLeaseDrawer, entityID, formInstance]);

  return (
    <>
      <Button
        data-tour="lease-new-button"
        onClick={toggleNewLeaseDrawer}
        type="primary"
        ghost
        loading={isSubmitting}
        disabled={isSubmitting}
      >
        Create Lease
      </Button>

      <Drawer
        open={showNewLeaseDrawer}
        onClose={toggleNewLeaseDrawer}
        width={500}
      >
        <Form layout="vertical" form={formInstance} onFinish={handleSubmit}>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label={
                  <Typography.Text>
                    {t("Entity_ID")}{" "}
                    <Typography.Text type="secondary">{`(${entityListOptions.length})`}</Typography.Text>
                  </Typography.Text>
                }
                name="entityID"
                initialValue={entityID}
                rules={[
                  {
                    required: true,
                    message: t("Select_an_entity"),
                  },
                ]}
              >
                <Select
                  placeholder={t("Select_an_entity")}
                  disabled={isSubmitting}
                >
                  {entityListOptions.map((option) => {
                    return (
                      <Select.Option value={option.value} key={option.value}>
                        {option.text}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={6}>
            <Col span={12}>
              <Form.Item
                name="tenantID"
                label="Tenant"
                rules={[{ required: true }]}
                style={{ marginBottom: 0 }}
              >
                <Select placeholder="Select a tenant">
                  {tenantList.map((tenant) => (
                    <Select.Option value={tenant.id} key={tenant.id}>
                      <TenantNameTag
                        authID={tenant.authUID || ""}
                        name={`${tenant.firstName} ${tenant.lastName}`}
                        status={
                          tenant.status || APITypes.TenantStatusType.InActive
                        }
                        authType={
                          tenant.type || APITypes.TenantType.ShadowTenant
                        }
                      />
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <NavButton to={ROUTES.addTenant} type="link">
                {t("Add_Tenant")}
              </NavButton>
            </Col>

            <Col span={12}>
              <Form.Item
                name="extraOccupants"
                label="Extra Occupants Information"
                tooltip="Separate occupant name with a comma"
              >
                <Select tokenSeparators={[","]} mode="tags" placeholder="" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={6}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Lease Type"
                rules={[{ required: true }]}
              >
                <Select onChange={handleLeaseTypeChange}>
                  {LeaseTypeOptionsList.map((type) => (
                    <Select.Option key={type} value={type}>
                      <LeaseTypeTag type={type} />
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* TODO: add this in the future */}
            {/* <Col span={12}>
              <Form.Item
                name="afterEnd"
                label="After End Date"
                rules={[{ required: true }]}
                valuePropName="checked"
              >
                <Checkbox
                  disabled={
                    formInstance.getFieldValue("type") ===
                    APITypes.LeaseTypeOptions.Monthly
                  }
                  onChange={toggleBecomeMonthtoMonth}
                  value={becomeMonthtoMonth}
                >
                  Month-Month
                </Checkbox>
              </Form.Item>
            </Col> */}
          </Row>

          <Row gutter={6}>
            <Col span={12}>
              <Form.Item
                name="startDate"
                label="Start Date"
                rules={[{ required: true }]}
              >
                <DatePicker onChange={handleStartDateChange} />
              </Form.Item>
            </Col>

            {becomeMonthtoMonth === true ? (
              <Col span={12}>
                <Form.Item
                  name="markedDate"
                  label="Marked Date"
                  tooltip="Date at which the lease type will change if applicable"
                >
                  <DatePicker />
                </Form.Item>
              </Col>
            ) : (
              <Col span={12}>
                <Form.Item
                  name="endDate"
                  label="End Date"
                  tooltip="Date at which the lease will definitively end if applicable"
                >
                  <DatePicker />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={6}>
            <Col span={24}>
              <Form.Item
                name="normalizeRent"
                valuePropName="checked"
                initialValue={false}
                tooltip={t(
                  "Normalize_rent_tooltip",
                  "If the lease starts mid-month, charge a prorated stub through month-end, then full calendar months. Leave unchecked to keep anniversary periods (e.g. 10th–9th).",
                )}
              >
                <Checkbox>
                  {t(
                    "Normalize_rent_to_calendar_months",
                    "Normalize rent to calendar months",
                  )}
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <div
            style={{
              borderTop: "1px solid lightgrey",
              display: "flex",
              justifyContent: "space-between",
              paddingTop: "1rem",
            }}
          >
            <Button onClick={() => formInstance.resetFields()}>
              {t("Reset_Form")}
            </Button>
            <Popconfirm
              title={t("Confirm_changes")}
              onConfirm={() => {
                formInstance.submit();
                toggleSubmitConfirm();
              }}
              open={showSubmitConfirm}
              onCancel={toggleSubmitConfirm}
            >
              <Button
                type="primary"
                onClick={toggleSubmitConfirm}
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {t("Submit")}
              </Button>
            </Popconfirm>
          </div>
        </Form>
      </Drawer>
    </>
  );
};
